import { useParams, Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { animeList } from '@/mocks/anime';
import AnimeCard from '@/components/base/AnimeCard';
import EmptyState from '@/components/base/EmptyState';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === id);

  useSeo(
    anime ? `${anime.title}详情 - 妖精动漫` : '动漫详情 - 妖精动漫',
    anime ? `${anime.summary}。${anime.title}作品详情、标签与相关推荐，尽在妖精动漫。` : '该动漫信息不存在或已下架。',
    anime ? `${anime.title},${anime.type},${anime.region}动漫,妖精动漫` : '妖精动漫',
  );

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="该动漫信息不存在或已下架"
          description="你访问的作品可能已被移除，或者链接有误。返回首页继续浏览其他作品吧。"
          actionText="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const related = animeList
    .filter((a) => a.id !== anime.id && (a.type === anime.type || a.region === anime.region || a.tags.some((t) => anime.tags.includes(t))))
    .slice(0, 4);

  const meta = [
    { label: '类型', value: anime.type, link: `/category?type=${encodeURIComponent(anime.type)}` },
    { label: '地区', value: anime.region, link: `/category?region=${encodeURIComponent(anime.region)}` },
    { label: '年份', value: `${anime.year}年` },
    { label: '状态', value: anime.status },
    { label: '集数', value: `${anime.episodes} 集` },
  ];

  return (
    <div className="w-full">
      <div className="relative w-full h-[280px] md:h-[380px] bg-primary-950 overflow-hidden">
        <img src={anime.banner} alt={`${anime.title}背景图`} title={`${anime.title} - 妖精动漫`} className="w-full h-full object-top opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/30"></div>
        <nav className="absolute top-6 left-4 md:left-8 flex flex-wrap items-center gap-2 text-xs md:text-sm text-background-300">
          <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category" className="hover:text-accent-400 transition-colors cursor-pointer">动漫分类</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-background-100">{anime.title}</span>
        </nav>
      </div>

      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 -mt-24 md:-mt-32 relative z-10">
          <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200 border-2 border-background-50">
              <img src={anime.cover} alt={`${anime.title}封面图`} title={`${anime.title} - 妖精动漫`} className="w-full h-full object-top" />
            </div>
          </div>

          <div className="flex-1 pt-2 md:pt-28 text-center md:text-left">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">{anime.type}</span>
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">{anime.region}动漫</span>
              <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs">{anime.year}年</span>
              <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs">{anime.status}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">{anime.title}</h1>
            {anime.rating > 0 && (
              <div className="mt-3 flex items-center justify-center md:justify-start gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-accent-50 text-accent-700 text-sm font-semibold">
                  <i className="ri-star-fill"></i>
                  {anime.rating.toFixed(1)}
                </span>
                <span className="text-xs text-foreground-400">示例评分（虚构数据）</span>
              </div>
            )}
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto md:mx-0">{anime.description}</p>

            <div className="mt-5 flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-sm">
              {meta.map((m) => (
                <span key={m.label} className="text-foreground-500">
                  {m.label}：
                  {m.link ? (
                    <Link to={m.link} className="text-primary-600 hover:text-accent-600 transition-colors cursor-pointer">{m.value}</Link>
                  ) : (
                    <span className="text-foreground-800">{m.value}</span>
                  )}
                </span>
              ))}
              <span className="text-foreground-500">热度：<span className="text-foreground-800">{(anime.views / 10000).toFixed(1)}万</span></span>
            </div>
          </div>
        </div>

        <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-4">
                <i className="ri-book-open-line text-primary-600"></i>剧情介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-7">{anime.description}</p>
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">作品标签</h3>
                <div className="flex flex-wrap gap-2">
                  {anime.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-primary-50 border border-primary-100 p-5">
              <h2 className="text-base font-bold text-foreground-950 mb-3">数据速览</h2>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">收藏量</span>
                  <span className="font-semibold text-foreground-900">{(anime.favorites / 10000).toFixed(1)}万</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">浏览热度</span>
                  <span className="font-semibold text-foreground-900">{(anime.views / 10000).toFixed(1)}万</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">更新时间</span>
                  <span className="font-semibold text-foreground-900">{anime.updatedAt}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-500">集数</span>
                  <span className="font-semibold text-foreground-900">{anime.episodes} 集</span>
                </li>
              </ul>
              <p className="mt-4 pt-4 border-t border-primary-100 text-xs text-foreground-400 leading-relaxed">
                本站为内容展示演示站点，以上数据均为示例，仅用于展示页面效果。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-10 md:mt-14">
            <h2 className="flex items-center gap-2 text-lg md:text-xl font-bold text-foreground-950 mb-5">
              <i className="ri-movie-2-line text-accent-500"></i>相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((a) => (
                <AnimeCard key={a.id} anime={a} />
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 mb-4">
          <Link to="/category" className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i>返回分类页
          </Link>
        </div>
      </div>
    </div>
  );
}